import { z } from 'zod';
import { isComputedField, OBJECT_NAMES, type ObjectDef, type ObjectName } from './objects';
import { filterSchema } from './validation';

// ---------------------------------------------------------------------------
// Approval processes: a record is submitted, matching the first active process whose entry
// conditions it meets; it then goes through up to five steps, each decided by one approver
// (the first response counts). The record is locked while the request is pending.
// ---------------------------------------------------------------------------

const uuid = z.string().uuid();

export const APPROVAL_PROCESSES_PER_OBJECT = 20;
export const APPROVAL_MAX_STEPS = 5;

/**
 * Who decides a step: one user; any member of a public group; or the submitter's manager,
 * meaning any active user in the role directly above the submitter's role.
 */
export const APPROVER_TYPES = ['user', 'group', 'manager'] as const;
export type ApproverType = (typeof APPROVER_TYPES)[number];

export const approvalStepSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    approverType: z.enum(APPROVER_TYPES),
    /** A user (approverType 'user') or a group (approverType 'group'). */
    approverId: uuid.nullable().optional(),
  })
  .strict()
  .refine((s) => s.approverType === 'manager' || !!s.approverId, { path: ['approverId'], message: 'Choose who approves this step' });
export type ApprovalStep = z.infer<typeof approvalStepSchema>;

/** Sets a field when the request is finally approved or rejected (system context, like workflow updates). */
export const approvalFieldUpdateSchema = z.object({ field: z.string().min(1).max(64), value: z.unknown() }).strict();
export type ApprovalFieldUpdate = z.infer<typeof approvalFieldUpdateSchema>;

export const approvalProcessSchema = z
  .object({
    objectName: z.enum(OBJECT_NAMES),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(1000).nullable().optional(),
    /** Entry conditions: a record can be submitted to this process only when all match. */
    conditions: z.array(filterSchema).max(10).default([]),
    steps: z.array(approvalStepSchema).min(1).max(APPROVAL_MAX_STEPS),
    onApprove: z.array(approvalFieldUpdateSchema).max(10).default([]),
    onReject: z.array(approvalFieldUpdateSchema).max(10).default([]),
    isActive: z.boolean().default(true),
  })
  .strict();
export type ApprovalProcessInput = z.infer<typeof approvalProcessSchema>;

export interface ApprovalProcess extends ApprovalProcessInput {
  id: string;
  sortOrder: number;
  version: number;
  updatedAt: string;
  pendingCount: number;
}

export const approvalSubmitSchema = z
  .object({ comment: z.string().trim().max(1000).optional(), processId: uuid.optional() })
  .strict();
export const approvalDecisionSchema = z.object({ comment: z.string().trim().max(1000).optional() }).strict();
export const approvalReorderSchema = z.object({ objectName: z.enum(OBJECT_NAMES), ids: z.array(uuid).min(1).max(APPROVAL_PROCESSES_PER_OBJECT) }).strict();

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'recalled';

export interface ApprovalAction {
  id: string;
  step: number | null;
  action: 'submitted' | 'approved' | 'rejected' | 'recalled';
  actorId: string;
  actorName: string;
  comment: string | null;
  /** Decided by an administrator (Modify All) rather than an assigned approver. */
  asAdmin: boolean;
  createdAt: string;
}

export interface ApprovalRequest {
  id: string;
  processId: string | null;
  processName: string;
  objectName: ObjectName;
  recordId: string;
  recordName: string;
  status: ApprovalStatus;
  /** 1-based; the step waiting for a decision while pending. */
  currentStep: number;
  stepCount: number;
  stepName: string;
  approverNames: string[];
  submittedBy: string;
  submittedByName: string;
  submittedAt: string;
  finishedAt: string | null;
  actions: ApprovalAction[];
  canDecide: boolean;
  canRecall: boolean;
}

/** Structural checks against the object's fields (update values are checked by type on the server). */
export function approvalProcessIssues(p: ApprovalProcessInput, obj: ObjectDef): string[] {
  const out: string[] = [];
  const field = (n: string) => obj.fields.find((f) => f.name === n);
  for (const c of p.conditions) if (!field(c.field)) out.push(`Unknown condition field "${c.field}"`);
  for (const u of [...p.onApprove, ...p.onReject]) {
    const f = field(u.field);
    if (!f || f.readOnly || isComputedField(f) || f.type === 'stage' || f.lookup === 'pipeline' || f.name === 'ownerId') {
      out.push(`"${u.field}" can't be set by an approval`);
    }
  }
  return out;
}
